import React, { useCallback, useMemo, useState } from "react";
import type { Moment } from "moment";
import {
  Checkbox,
  FormLabel,
  Typography,
  IconButton,
  Box,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useFormikContext } from "formik";

import RpDatePickerBase from "@/components/muiCustom/date/RpDatePicker";
import RpFormErrorBase from "@/components/muiCustom/error/RpFormError";
import RpCurrencyFieldBase from "@/components/muiCustom/textField/RpCurrencyField";
import { formattedAmount, getFormatDate } from "@/helpers/utils";
import CurrentCreativesBase from "./CurrentCreatives";
import { extendCampaignHelper } from "@/features/campaigns/digital/extend-campaign/utils/extendCampaignHelper";
import CreativeUpdateEligibilityDaysBase from "./CreativeUpdateEligibilityDays";
import ExtendHeaderTooltipBase from "./ExtendHeaderTooltip";
import { RpSweetAlert } from "@/components/alerts/RpSweetAlert";
import { EstimatedImpressionsText } from "@/features/campaigns/shared/components/EstimatedImpressions";
import {
  getImpressionEstimation,
  getEstimatedImpressions,
  type EstimatedImpressionChannel,
} from "@/features/campaigns/shared/helpers/estimatedImpressionsHelper";

type Id = number | string;
type DateValue = string | Date | Moment | null | undefined;

type CampaignCreative = {
  id?: Id;
  is_selected?: boolean;
  channels?: EstimatedImpressionChannel[];
  [key: string]: unknown;
};

type ExtendEnrollment = {
  start_date?: DateValue;
  end_date?: DateValue;
  total_budget?: number | string | null;
};

type ExtendLocationInfo = {
  location_id: Id;
  location_name?: string;
  start_date?: DateValue;
  end_date?: DateValue;
  extend_end_date?: DateValue;
  total_budget?: number | string | null;
  subscription?: {
    subscription_end_date?: DateValue;
  } | null;
  creative_data?: CampaignCreative[];
  packages_data?: CampaignCreative[];
  latest_extentded_enrollments?: ExtendEnrollment | null;
  extend_enrollments?: ExtendEnrollment[] | null;
  [key: string]: unknown;
};

type ExtendCampaignData = {
  evergreen_participant_min_value?: number | string | null;
  evergreen_participant_max_value?: number | string | null;
  availability_window?: string | null;
  availability_window_date?: {
    window_start_date?: DateValue;
    window_end_date?: DateValue;
  } | null;
  location_fees_covered?: boolean | null;
  contract_end_date?: DateValue;
  [key: string]: unknown;
};

type ExtendLocationFormValue = {
  creatives?: CampaignCreative[];
  extend_start_date?: DateValue;
  extend_end_date?: DateValue;
  extend_budget?: number | string | null;
  [key: string]: unknown;
};

type ExtendLocationFormikValues = {
  selectedLoc?: Record<Id, ExtendLocationFormValue>;
};

type ExtendLocationFormikErrors = {
  selectedLoc?: Record<
    Id,
    {
      extend_start_date?: React.ReactNode;
      extend_end_date?: React.ReactNode;
      extend_budget?: React.ReactNode;
    }
  >;
};

type ExtendLocationFormProps = {
  location_id: Id;
  lc?: ExtendLocationFormValue;
  loc: ExtendLocationInfo;
  campaignData: ExtendCampaignData;
  showLocationCheckBox?: boolean;
  checkedLocations: Id[];
  setCheckedLocations: React.Dispatch<React.SetStateAction<Id[]>>;
  disableLocations: Id[];
};

type MobileLabelProps = {
  label: string;
  displayTooltip?: boolean;
};

const RpDatePicker = RpDatePickerBase as React.ComponentType<
  Record<string, unknown>
>;
const RpCurrencyField = RpCurrencyFieldBase as React.ComponentType<
  Record<string, unknown>
>;
const RpFormError = RpFormErrorBase as React.ComponentType<
  Record<string, unknown>
>;
const CurrentCreatives = CurrentCreativesBase as React.ComponentType<
  Record<string, unknown>
>;
const CreativeUpdateEligibilityDays =
  CreativeUpdateEligibilityDaysBase as React.ComponentType<
    Record<string, unknown>
  >;
const ExtendHeaderTooltip = ExtendHeaderTooltipBase as React.ComponentType<
  Record<string, unknown>
>;

const MobileLabel = ({ label, displayTooltip = false }: MobileLabelProps) => {
  return (
    <FormLabel
      className="fw-sb text-dark title"
      sx={{ display: { xs: "block", sm: "none" } }}
    >
      {label}
      {displayTooltip && <ExtendHeaderTooltip label={label} />}
    </FormLabel>
  );
};

function getExtensionEstimateChannels({
  loc,
  lc,
}: {
  loc: ExtendLocationInfo;
  lc?: ExtendLocationFormValue;
}): EstimatedImpressionChannel[] {
  const hasPackageData = Array.isArray(loc?.packages_data);
  const creatives = hasPackageData
    ? (loc.packages_data || []).filter(
        (creative) => creative.is_selected === true
      )
    : [
        ...((lc?.creatives as CampaignCreative[] | undefined) || []),
        ...((loc?.creative_data as CampaignCreative[] | undefined) || []),
      ];
  const channelsByKey = new Map<string, EstimatedImpressionChannel>();

  creatives.forEach((creative) => {
    creative?.channels?.forEach((channel) => {
      const id = channel.fk_channel_id ?? channel.channel_id ?? channel.id;
      const key = String(id ?? "");

      if (key) {
        channelsByKey.set(key, { ...channel, id });
      }
    });
  });

  return Array.from(channelsByKey.values());
}

function ExtendLocationForm({
  location_id,
  lc,
  loc,
  campaignData,
  showLocationCheckBox,
  checkedLocations,
  setCheckedLocations,
  disableLocations,
}: ExtendLocationFormProps) {
  const { setFieldValue, setFieldTouched, validateField, errors, values } =
    useFormikContext<ExtendLocationFormikValues>();
  const formErrors = errors as ExtendLocationFormikErrors;
  const selectedLocationStartDate =
    values?.selectedLoc?.[location_id]?.extend_start_date;
  const selectedLocationEndDate =
    values?.selectedLoc?.[location_id]?.extend_end_date;
  const selectedLocationExtendBudget =
    values?.selectedLoc?.[location_id]?.extend_budget;
  const hasCompleteExtensionDetails = Boolean(
    selectedLocationStartDate &&
      selectedLocationEndDate &&
      selectedLocationExtendBudget
  );
  const selectedLocationExtendBudgetError =
    formErrors?.selectedLoc?.[location_id]?.extend_budget;
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);
  const estimatedImpressions = useMemo(() => {
    return getEstimatedImpressions({
      budget: selectedLocationExtendBudget,
      impressionEstimation: getImpressionEstimation(campaignData),
      channels: getExtensionEstimateChannels({ loc, lc }),
      allocateBudgetAcrossBenchmarkedChannels: true,
    });
  }, [campaignData, lc, loc, selectedLocationExtendBudget]);

  const dateConstraints = useMemo(() => {
    // Get the original min/max dates (existing logic unchanged)
    const { minStartDate, maxStartDate } =
      extendCampaignHelper.getExtendCampaignMinMaxStartDate(loc, campaignData);
    const { minEndDate, maxEndDate } =
      extendCampaignHelper.getExtendCampaignMinMaxEndDate(
        loc?.extend_end_date ? loc?.extend_end_date : lc?.extend_start_date,
        loc,
        campaignData
      );

    return {
      minStartDate,
      maxStartDate,
      minEndDate,
      maxEndDate,
    };
  }, [lc?.extend_start_date, loc, campaignData]);

  const daySlots = useMemo(() => {
    const startDateForCreative = loc?.extend_end_date
      ? loc?.extend_end_date
      : lc?.extend_start_date;

    // If no start date selected, return empty ranges
    if (!startDateForCreative) {
      return (props: Record<string, unknown>) => (
        <CreativeUpdateEligibilityDays
          {...props}
          canUpdateDate={[]}
          canNotUpdateDate={[]}
          startDate={lc?.extend_start_date}
        />
      );
    }

    // Use extension-aware creative eligibility
    const { canNotUpdateDate, canUpdateDate } =
      extendCampaignHelper.getCreativeUpdateEligibilityDaysWithExtensions(
        startDateForCreative,
        loc,
        campaignData
      );

    return (props: Record<string, unknown>) => (
      <CreativeUpdateEligibilityDays
        {...props}
        canUpdateDate={canUpdateDate}
        canNotUpdateDate={canNotUpdateDate}
        startDate={lc?.extend_start_date}
      />
    );
  }, [lc?.extend_start_date, loc, campaignData]);

  const handleOnSelectStartDate = useCallback(
    (date: Moment | null) => {
      if (!date) {
        setFieldValue(`selectedLoc.${location_id}.extend_start_date`, "");
        setFieldValue(`selectedLoc.${location_id}.extend_end_date`, "");
        setFieldValue(`selectedLoc.${location_id}.extend_budget`, "");
        setEndDatePickerOpen(false);
        return;
      }

      // Validate: Check if selected date falls in any extended range
      const conflictingRange = extendCampaignHelper.isDateInExtendedRanges(
        date,
        loc
      );
      if (conflictingRange) {
        const locationName = loc?.location_name || "Location";
        RpSweetAlert.fire({
          title: "Date Not Eligible",
          text: `Location: ${locationName} already extended from ${getFormatDate(conflictingRange.start_date)} to ${getFormatDate(conflictingRange.end_date)}. Selected dates overlap this range. Please choose different dates.`,
          icon: "error",
          confirmButtonText: "OK",
          showCancelButton: false,
        });
        return; // Prevent updating form state
      }

      setFieldValue(
        `selectedLoc.${location_id}.extend_start_date`,
        date.format("YYYY-MM-DD")
      );
      setFieldValue(`selectedLoc.${location_id}.extend_end_date`, "");
      setFieldValue(`selectedLoc.${location_id}.extend_budget`, "");

      // Automatically open end date picker after start date is selected

      setTimeout(() => {
        setEndDatePickerOpen(true);
      }, 100);
    },
    [setFieldValue, location_id, loc]
  );

  const handleOnSelectEndDate = useCallback(
    (date: Moment | null) => {
      if (!date) {
        setFieldValue(`selectedLoc.${location_id}.extend_end_date`, "");
        return;
      }

      const startDate = selectedLocationStartDate;
      const endDateStr = date.format("YYYY-MM-DD");

      // Validate: Check if selected end date falls in any extended range
      const conflictingRange = extendCampaignHelper.isDateInExtendedRanges(
        date,
        loc
      );
      if (conflictingRange) {
        const locationName = loc?.location_name || "Location";
        RpSweetAlert.fire({
          title: "Date Not Eligible",
          text: `Location: ${locationName} already extended from ${getFormatDate(conflictingRange.start_date)} to ${getFormatDate(conflictingRange.end_date)}. Selected dates overlap this range. Please choose different dates.`,
          icon: "error",
          confirmButtonText: "OK",
          showCancelButton: false,
        });
        return; // Prevent updating form state
      }

      // Validate: Check if the date range overlaps with any extended range
      if (startDate) {
        const rangeConflict =
          extendCampaignHelper.validateDateRangeAgainstExtendedRanges(
            startDate,
            endDateStr,
            loc
          );
        if (rangeConflict) {
          const locationName =
            rangeConflict.locationName || loc?.location_name || "Location";
          const rangesText = rangeConflict.conflictingRanges
            .map(
              (range) =>
                `${getFormatDate(range.start_date)} to ${getFormatDate(range.end_date)}`
            )
            .join(" and ");
          RpSweetAlert.fire({
            title: "Date Range Not Eligible",
            text: `Location: ${locationName} already extended from ${rangesText}. Selected dates overlap this range. Please choose different dates.`,
            icon: "error",
            confirmButtonText: "OK",
            showCancelButton: false,
          });
          return; // Prevent updating form state
        }
      }

      setFieldValue(`selectedLoc.${location_id}.extend_end_date`, endDateStr);

      // check if location eligible to update creative (existing logic unchanged)
      if (startDate && endDateStr) {
        const canUpdateCreative = extendCampaignHelper.findCanUpdateCreative(
          startDate,
          endDateStr,
          campaignData
        );
        setFieldValue(
          `selectedLoc.${location_id}.can_update_creatives`,
          canUpdateCreative
        );

        // Trigger budget validation to show recommendation message when end date is selected
        setFieldTouched(`selectedLoc.${location_id}.extend_budget`, true);
        validateField("selectedLoc");
      }
    },
    [
      setFieldTouched,
      setFieldValue,
      location_id,
      selectedLocationStartDate,
      validateField,
      campaignData,
      loc,
    ]
  );

  // Function to disable dates that fall within extended enrollment ranges
  // minDate/maxDate props handle dates outside the main window
  const shouldDisableExtendedDate = useCallback(
    (date: Moment) => {
      return extendCampaignHelper.shouldDisableExtendedDate(date, loc);
    },
    [loc]
  );

  const handleOnBoostBudgetChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const v = event.target.value;
      setFieldValue(`selectedLoc.${location_id}.extend_budget`, v);
      setFieldTouched(`selectedLoc.${location_id}.extend_budget`, true);
    },
    [setFieldValue, setFieldTouched, location_id]
  );

  const handleClearEndDate = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation(); // Prevent opening the calendar
      setFieldValue(`selectedLoc.${location_id}.extend_end_date`, "");
      setFieldValue(`selectedLoc.${location_id}.extend_budget`, "");
      setFieldValue(`selectedLoc.${location_id}.can_update_creatives`, false);
      setEndDatePickerOpen(false);
    },
    [setFieldValue, location_id]
  );

  const clearButtonStyles = useMemo(
    () => ({
      position: "absolute",
      right: "28px",
      top: "50%",
      transform: "translateY(-50%)",
      padding: "2px",
      zIndex: 2,
      "&:hover": {
        backgroundColor: "var(--neutral-100)",
      },
    }),
    []
  );

  return (
    <div className="card h-100 ">
      <div className="card-body py-3 px-3">
        <div className="row align-items-start justify-content-start justify-content-lg-center g-0">
          {showLocationCheckBox && (
            <div
              className={`col-1 col-lg-1 mb-3 ml-3 ml-lg-0 align-items-center justify-content-center ${showLocationCheckBox ? "d-flex" : "d-none"}`}
            >
              <Checkbox
                color="primary"
                checked={checkedLocations.includes(loc.location_id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setCheckedLocations((prev) => [...prev, loc.location_id]);
                  } else {
                    setCheckedLocations((prev) =>
                      prev.filter((id) => id !== loc.location_id)
                    );
                  }
                }}
                className="m-0 p-0 "
                disabled={disableLocations.includes(loc.location_id)}
              />
            </div>
          )}
          <div
            className={`col-6 col-lg-${showLocationCheckBox ? "1" : "2"} mt-2 mt-lg-0`}
          >
            <div className="d-lg-none title">Location: </div>

            <span className="title-val fw-b text-break">
              {loc?.location_name}
            </span>
            {loc?.latest_extentded_enrollments?.start_date &&
              loc?.latest_extentded_enrollments?.end_date && (
                <Typography
                  variant="caption"
                  className="fs-10 fw-sb"
                  component="div"
                >
                  Previously extended for :{" "}
                  <span className="text-nowrap">
                    {`${getFormatDate(loc?.latest_extentded_enrollments?.start_date, "MM/DD/YYYY")} - ${getFormatDate(loc?.latest_extentded_enrollments?.end_date, "MM/DD/YYYY")}`}
                    {loc?.latest_extentded_enrollments?.total_budget &&
                      `, ${formattedAmount(loc?.latest_extentded_enrollments?.total_budget)}`}
                  </span>
                </Typography>
              )}
          </div>
          <div className="col-6 col-lg-1 mt-2 mt-lg-0">
            <div className="d-lg-none title">Start Date: </div>
            <span className="title-val">{getFormatDate(loc?.start_date)}</span>
          </div>
          <div className="col-6 col-lg-1 mt-2 mt-lg-0">
            <div className="d-lg-none title">End Date: </div>
            <span className="title-val">{getFormatDate(loc?.end_date)}</span>
          </div>
          <div className={`col-7 col-lg-1 fw-sb pr-lg-1 mt-1`}>
            <MobileLabel label="Current Creatives" />
            <div className="title-val">
              <CurrentCreatives
                creatives={lc?.creatives}
                campaignData={campaignData}
                locationInfo={loc}
              />
            </div>
          </div>
          <div className="col-6 col-lg-1 mt-2 mt-lg-0">
            <MobileLabel label="Current Budget" />
            <span className="title-val ">
              {formattedAmount(loc?.total_budget)}
            </span>
          </div>
          <div className="col-12 col-lg-2 mt-2 mt-lg-0">
            <MobileLabel label="New Start Date" displayTooltip={true} />
            <div className="title-val">
              <RpDatePicker
                value={lc?.extend_start_date || null}
                onChange={handleOnSelectStartDate}
                placeholder="Start Date"
                minDate={dateConstraints.minStartDate}
                maxDate={dateConstraints.maxStartDate}
                shouldDisableDate={shouldDisableExtendedDate}
                smallSize={true}
              />
              <RpFormError
                error={
                  formErrors?.selectedLoc?.[location_id]?.extend_start_date
                }
              />
            </div>
          </div>
          <div className="col-12 col-lg-2 mt-2 mt-lg-0">
            <MobileLabel label="New End Date" displayTooltip={true} />
            <Box className="title-val">
              <RpDatePicker
                value={lc?.extend_end_date || null}
                onChange={handleOnSelectEndDate}
                disabled={
                  !values?.selectedLoc?.[location_id]?.extend_start_date
                }
                placeholder="End Date"
                minDate={dateConstraints.minEndDate}
                maxDate={dateConstraints.maxEndDate}
                shouldDisableDate={shouldDisableExtendedDate}
                smallSize={true}
                open={endDatePickerOpen}
                onOpenChange={setEndDatePickerOpen}
                slots={{
                  day: daySlots,
                }}
              />
              {lc?.extend_end_date &&
                values?.selectedLoc?.[location_id]?.extend_start_date && (
                  <IconButton
                    onClick={handleClearEndDate}
                    size="small"
                    sx={clearButtonStyles}
                    aria-label="clear end date"
                  >
                    <Close
                      className="mx-1 mr-3 text-danger fw-sb cursor-p"
                      sx={{ fontSize: "18px" }}
                    />
                  </IconButton>
                )}
              <RpFormError
                error={formErrors?.selectedLoc?.[location_id]?.extend_end_date}
              />
            </Box>
          </div>
          <div className="col-12 col-lg-2 mt-2 mt-lg-0">
            <MobileLabel label="New Budget" />
            <div className="title-val">
              <RpCurrencyField
                name={`selectedLoc.${location_id}.extend_budget`}
                placeholder="$0.00 USD"
                value={values?.selectedLoc?.[location_id]?.extend_budget || ""}
                onChange={handleOnBoostBudgetChange}
                error={Boolean(
                  formErrors?.selectedLoc?.[location_id]?.extend_budget
                )}
                smallSize={true}
                decimalScale={0}
              />
              <RpFormError
                error={selectedLocationExtendBudgetError}
                greyColor={true}
              />
              {!selectedLocationExtendBudgetError &&
              hasCompleteExtensionDetails ? (
                <EstimatedImpressionsText
                  enabled={getImpressionEstimation(campaignData)?.enabled}
                  estimate={estimatedImpressions}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExtendLocationForm;
